/**
 *  _    ___   ___  ___  ___
 * | |  | _ ) / _ \| _ \/ __|
 * | |__| _ \| (_) |   / (_ |
 * |____|___(_)___/|_|_\\___|
 *        LoadBalancer.org Client
 * @copyright Copyright (c) 2025
 */
import { DE_DE, EN_GB, EN_US, ES_ES } from '../../languages';
import { DictionaryService } from './dictionary.service';
import { ErrorResponseModel } from '../../models';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { environment, ModulesEnum } from '../../../../environments/environment';

@Injectable()
export class LanguageService {
    /**
     * Currently selected language. en_us as default
     * @type {string}
     */
    private _currentLang: string = 'en_us';
    /**
     * Available languages
     */
    private dictionary = {
        ['de-de']: DE_DE,
        ['en-gb']: EN_GB,
        ['en-us']: EN_US,
        ['es-es']: ES_ES
    };
    /**
     * Help document json
     */
    private _help: object;

    /**
     * Help drawer reference json
     * @private
     */
    private _drawerReferences: string[] = [];

    constructor(
        private dictionaryService: DictionaryService,
        private httpClient: HttpClient,
        private logService: LogService
    ) {
    }

    /**
     * Get the current language
     * @returns {string}
     */
    get currentLang(): string {
        return this._currentLang;
    }

    /**
     * Set the current language file.
     * @param {string} value
     */
    set currentLang(value: string) {
        this._currentLang = value;
    }

    /**
     * Get the current environment.
     */
    get environment(): any {
        return environment;
    }

    /**
     * Get the Modules ENUM, so we can use it in the view.
     */
    get modulesEnum(): any {
        return ModulesEnum;
    }

    /**
     * Get the help file.
     */
    get help(): any {
        return this._help;
    }

    /**
     * Set the help file.
     * @param file
     */
    set help(file: any) {
        this._help = file;
    }

    get drawerReferences(): string[] {
        return this._drawerReferences;
    }

    set drawerReferences(file: string[]) {
        this._drawerReferences = file;
    }

    /**
     * Page has drawer reference.
     * @param page
     */
    public pageHasDrawerReference(page: string): boolean {
        return this.drawerReferences.includes(page);
    }

    /**
     * Set language in the application.
     * @param {string} language
     */
    public setLanguage(language: string): void {
        const lang: string = this.formatLanguage(language);
        if (this.dictionary.hasOwnProperty(lang)) {
            this.logService.info('LanguageService', `Language has been set ${lang}.`);
            this.currentLang = lang;
        } else {
            this.logService.warn('LanguageService', `Missing language, defaulting to en-us ${lang}.`);
            this.currentLang = 'en-us';
        }

        if (!this.help) {
            this.getHelpFile();
            this.getDrawerReference();
        }
    }

    /**
     * Get a help item from the help document
     * @param {string} key
     * @returns {any}
     */
    public getHelpItemByKey(key: string): any {
        if (this._help !== undefined) {
            return this._help[key];
        }
    }

    /**
     * Return translated text
     * @param {string} key
     * @param placeholder
     * @returns {string}
     */
    public translate(key: string, placeholder?: string | number | any[]): string {
        if (this.dictionary[this.currentLang] && this.dictionary[this.currentLang][key]) {
            const text: string = this.dictionary[this.currentLang][key];

            return placeholder ? this.replacePlaceholder(text, placeholder) : text;
        } else if (this.dictionary['en-us'][key]) {
            const text: string = this.dictionary['en-us'][key];

            return placeholder ? this.replacePlaceholder(text, placeholder) : text;
        }

        // Return key with enclose in ** to show no value
        return '**' + key + '**';
    }

    /**
     * Replace placeholder.
     * @param text
     * @param placeholders
     * @private
     */
    private replacePlaceholder(text: string, placeholders: string | number | any[]): string {
        if (!Array.isArray(placeholders)) {
            return text.replace('{{placeholder}}', placeholders.toString());
        } else {
            let index: number = 1;
            for (const placeholder of placeholders) {
                text = text.replace(`{{placeholder${index}}}`, placeholder.toString());
                index++;
            }

            return text;
        }
    }

    /**
     * Get the help file depending on the environment.
     * @private
     */
    private getHelpFile(): void {
        const helpPath: string = this.getHelpTextPath();
        const url: string = this.getHelpTextUrl(helpPath);
        this.httpClient.get(url)
            .subscribe({
                next: (response: object) => {
                    this._help = this.getFormattedHelpText(response);
                },
                error: (error: ErrorResponseModel) => {
                    this.handleHelpError(error, helpPath);
                }
            });
    }

    /**
     * Get the help drawer depending on the environment.
     * @private
     */
    private getDrawerReference(): void {
        const helpPath: string = this.getHelpTextPath();
        const url: string = helpPath + 'drawers.json';
        this.httpClient.get(url)
            .subscribe({
                next: (response: string[]) => {
                    this.drawerReferences = response;
                },
                error: (error: ErrorResponseModel) => {
                    this.handleHelpError(error, helpPath);
                }
            });
    }

    /**
     * Get help text path.
     * @private
     */
    private getHelpTextPath(): string {
        return this.environment.module === this.modulesEnum.LOADBALANCER
            ? '/assets/loadbalancer/help/'
            : '/assets/centralisedManagement/help/';
    }

    /**
     * Get help text URL.
     * @param helpPath
     * @private
     */
    private getHelpTextUrl(helpPath: string): string {
        if (this.currentLang === 'en-gb') {
            return helpPath + 'en-us.json';
        }

        return helpPath + this.currentLang + '.json';
    }

    /**
     * Get formatted help text.
     * @param response
     * @private
     */
    private getFormattedHelpText(response: object): object {
        if (this.currentLang === 'en-gb') {
            const newResponse = {};
            for (const key in response) {
                if (response.hasOwnProperty(key) && typeof response[key] === 'object') {
                    newResponse[key] = newResponse[key] || {};
                    newResponse[key] = this.getFormattedHelpKeys(response[key], newResponse[key]);
                }
            }

            return newResponse;
        }

        return response;
    }

    /**
     * Get formatted help keys (title, summary, text).
     * @param response
     * @param newResponse
     * @private
     */
    private getFormattedHelpKeys(response: object, newResponse: object): object {
        for (const text in response) {
            if (response.hasOwnProperty(text)) {
                newResponse[text] = newResponse[text] || {};
                newResponse[text] = this.dictionaryService.removeUSSpelling(response[text]);
            }
        }

        return newResponse;
    }

    /**
     * Handle help error.
     * @param error
     * @param helpPath
     * @private
     */
    private handleHelpError(error: ErrorResponseModel, helpPath: string): void {
        this.logService.error('LanguageService', 'Unable to load '
            + this.currentLang + ' help document. Attempting to load default en-us..' + error?.message);
        if (this.currentLang !== 'en-us') {
            const newUrl = helpPath + 'en-us.json';
            this.httpClient.get(newUrl)
                .subscribe({
                    next: (res: object): void => {
                        this._help = res;
                    },
                    error: (): void => {
                        this._help = {};
                    }
                });
        } else {
            this.logService.error('LanguageService', 'Unable to load a help document.');
        }
    }

    /**
     * Format language string to match dictionary format.
     * @param language
     * @private
     */
    private formatLanguage(language: string): string {
        return language.replace('_', '-').toLowerCase();
    }
}
